import { type KeyboardEvent, type MouseEvent } from 'react';
import { Flex, cn } from '@kognitos/lattice';

import { catalogWorkstreamAccentVar } from '@/modules/process-catalog/workstream-accent';

/** The folder's design canvas. Everything below is expressed in these units. */
export const CATALOG_FOLDER_WIDTH = 132;
export const CATALOG_FOLDER_HEIGHT = 60;

/** How deep the paper sheet sits inside the folder, in px from the folder's top. */
const PAPER_TOP_BY_VARIANT = { closed: 30, peek: 18, open: 8 } as const;

/** Which of the three paper depths a folder is drawn at. */
export type TCatalogFolderVariant = keyof typeof PAPER_TOP_BY_VARIANT;

/** Tab width tracks the label, capped so it can't reach the right shoulder. */
export function catalogFolderTabWidth(name: string): number {
  return Math.min(112, Math.round(name.length * 5.6) + 20);
}

/**
 * Back-and-tab silhouette as one union path (two shapes would seam where they
 * overlap), left open at y=60 so the stroke never crosses the cropped base.
 *
 * @param {string} name - Label the tab is sized for
 * @returns {string} An SVG `d` attribute in the 132×60 canvas
 */
export function catalogFolderBackPath(name: string): string {
  const tw = catalogFolderTabWidth(name);
  return [
    'M0.5,60',
    'L0.5,5.5',
    'Q0.5,0.5 5.5,0.5',
    `L${tw - 5},0.5`,
    `Q${tw},0.5 ${tw},5.5`,
    `L${tw},10.5`,
    `Q${tw},15.5 ${tw + 5},15.5`,
    'L123.5,15.5',
    'Q129.5,15.5 129.5,21.5',
    'L129.5,60',
  ].join(' ');
}

/** A `color-mix` of the workstream's accent token at `pct` opacity. */
function accentMix(accentVar: string, pct: number): string {
  // `in srgb` and not oklch: the handoff's percentages were tuned in sRGB, and
  // oklch interpolation lands visibly lighter at the same number.
  return `color-mix(in srgb, var(${accentVar}) ${pct}%, transparent)`;
}

export interface ICatalogFolderProps {
  /** Real workstream key — resolves the accent, even when {@link label} differs. */
  workstream: string;
  /** The tab's label. Defaults to {@link workstream}. */
  label?: string;
  /** Paper depth. The banner uses `peek`. */
  variant?: TCatalogFolderVariant;
  /** Hovered state, owned by the caller since it also drives the banner's fan. */
  hovered?: boolean;
  /** Raised when the pointer or keyboard focus enters. Drives {@link hovered}. */
  onMouseEnter?: () => void;
  /** Raised when the pointer or keyboard focus leaves. */
  onMouseLeave?: () => void;
  /**
   * Raised on click, Enter or Space. Given, the folder becomes a named control
   * with its own tab stop; omitted, it stays decorative.
   */
  onSelect?: () => void;
  className?: string;
}

/**
 * One translucent, colour-tinted folder in the process-catalog banner strip.
 *
 * Four stacked layers, back to front: the back-and-tab silhouette (SVG), the
 * tab label, a white paper sheet, and the translucent front flap.
 *
 * @param props - The workstream, its display label, paper depth and hover state.
 * @returns The folder.
 */
export function CatalogFolder({
  workstream,
  label,
  variant = 'peek',
  hovered = false,
  onMouseEnter,
  onMouseLeave,
  onSelect,
  className,
}: ICatalogFolderProps) {
  const name = label ?? workstream;
  const accentVar = catalogWorkstreamAccentVar(workstream);
  // The sheet rises 2px on hover. The bottom stays anchored, so it is the top
  // edge that moves and the folder's base stays white.
  const paperTop = PAPER_TOP_BY_VARIANT[variant] - (hovered ? 2 : 0);

  // The click never belongs to the banner behind it: bubbling would have it
  // reopen at rest and lose the folder the user aimed at.
  const select = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
    onSelect?.();
  };

  return (
    <div
      // A div, not a Button: Lattice's Button sizes descendant svgs to 16px,
      // which would crush the 132×60 silhouette. Role and keys supplied here.
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      // The layers below are all aria-hidden, so the name has to come from here.
      aria-label={onSelect ? name : undefined}
      className={cn(
        'relative h-[60px] w-[132px] shrink-0',
        onSelect && 'focus-ring rounded-[8px]',
        className,
      )}
      data-testid={`catalog-folder-${workstream}`}
      data-hovered={hovered ? 'true' : 'false'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // Focus fans the pills too, so a keyboard user sees what a hover shows.
      onFocus={onMouseEnter}
      onBlur={onMouseLeave}
      onClick={onSelect ? select : undefined}
      onKeyDown={event => {
        if (!onSelect) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select(event);
        }
      }}
    >
      <svg
        width={CATALOG_FOLDER_WIDTH}
        height={CATALOG_FOLDER_HEIGHT}
        viewBox="0 0 132 60"
        fill="none"
        aria-hidden
        className="absolute top-0 left-0"
      >
        <path
          d={catalogFolderBackPath(name)}
          fill={accentMix(accentVar, 24)}
          stroke={accentMix(accentVar, 42)}
          strokeWidth={1}
        />
      </svg>

      <span
        aria-hidden
        className="absolute top-0 left-0 max-w-[112px] overflow-hidden pt-[5px] pr-[9px] pb-[4px] pl-[9px] text-[9px] leading-none font-semibold text-ellipsis whitespace-nowrap text-foreground"
      >
        {name}
      </span>

      {/* Two skeleton bars, standing in for the page's content. */}
      <Flex
        direction="column"
        align="stretch"
        position="absolute"
        overflow="hidden"
        gap={1}
        paddingX={2}
        paddingY={1.5}
        // eslint-disable-next-line no-restricted-syntax -- theme-fixed: the paper sheet is paper in both themes
        className="right-[4px] bottom-0 left-[4px] rounded-t-[4px] bg-[#f7f7f4] transition-[top] duration-[240ms] ease-out"
        style={{ top: `${paperTop}px` }}
        aria-hidden
      >
        {/* eslint-disable-next-line no-restricted-syntax -- theme-fixed: ink on the fixed paper above */}
        <span className="h-[4px] w-[72%] rounded-[2px] bg-[#d9d9d3]" />
        {/* eslint-disable-next-line no-restricted-syntax -- theme-fixed: ink on the fixed paper above */}
        <span className="h-[4px] w-[50%] rounded-[2px] bg-[#e4e4de]" />
      </Flex>

      {/* The front flap. Translucent and blurred, so the sheet reads as being behind it. */}
      <div
        aria-hidden
        className="absolute top-[21px] right-0 bottom-0 left-0 rounded-tr-[7px] border border-b-0 backdrop-blur-[2.5px] transition-[background-color] duration-[240ms] ease-out"
        style={{
          backgroundColor: accentMix(accentVar, hovered ? 21 : 16),
          borderColor: accentMix(accentVar, 34),
        }}
      />
    </div>
  );
}
